import { useState, useEffect, useMemo } from 'react';
import { NSModal, NSInputNumberStepper, NSTable } from 'bricks';
import './LeasingCostReservesModal.scss';
import { numeralFormatter, numeralFormatterCurrency } from 'ns_libs/formatter';
import { useDebounce } from 'hooks/useDebounce';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import { ILeasingCostReserves } from '../../../../../types';
import { isModelWithLeasingCostReserves } from 'helpers/proForma';

interface ILeasingCostReservesModalProps {
    isOpen: boolean;
    toggle: () => void;
}

interface ILeasingCostData {
    renewalProbability: number;
    averageMarketRent: number;
    leaseTerm: number;
    tenantImprovements: number;
    leasingCommissions: number;
    downtimeFreeRent: number;
}

const LeasingCostReservesModal = ({ isOpen, toggle }: ILeasingCostReservesModalProps) => {
    const {
        showCents,
        leasingCostReserves,
        handleUpdateLeasingCostReserves,
        handlePreviewLeasingCostReserves,
        incomeSummary,
        proForma,
    } = useProFormaSocketContext();

    const [previewData, setPreviewData] = useState<ILeasingCostReserves | null>(null);

    const areaType = isModelWithLeasingCostReserves(proForma.proFormaModelId) ? 'GSF' : 'SF';

    const defaultAverageMarketRent = useMemo(
        () => incomeSummary?.totalRentalIncome?.totalValuePerSfPerYear || 0,
        [incomeSummary?.totalRentalIncome?.totalValuePerSfPerYear],
    );

    const [newLeaseData, setNewLeaseData] = useState<ILeasingCostData>({
        renewalProbability: leasingCostReserves?.newLeaseRenewalProbability || 0,
        averageMarketRent: leasingCostReserves?.newLeaseAverageRetailMarketRent || defaultAverageMarketRent || 0,
        leaseTerm: leasingCostReserves?.newLeaseLeaseTermYears || 0,
        tenantImprovements: leasingCostReserves?.newLeaseTenantImprovementsPerSf || 0,
        leasingCommissions: leasingCostReserves?.newLeaseLeasingCommissionsPercentage || 0,
        downtimeFreeRent: leasingCostReserves?.newLeaseDowntimeFreeRentMonths || 0,
    });

    const [renewalLeaseData, setRenewalLeaseData] = useState<ILeasingCostData>({
        renewalProbability: leasingCostReserves?.renewalLeaseRenewalProbability || 0,
        averageMarketRent: leasingCostReserves?.renewalLeaseAverageRetailMarketRent || defaultAverageMarketRent || 0,
        leaseTerm: leasingCostReserves?.renewalLeaseLeaseTermYears || 0,
        tenantImprovements: leasingCostReserves?.renewalLeaseTenantImprovementsPerSf || 0,
        leasingCommissions: leasingCostReserves?.renewalLeaseLeasingCommissionsPercentage || 0,
        downtimeFreeRent: leasingCostReserves?.renewalLeaseDowntimeFreeRentMonths || 0,
    });

    useEffect(() => {
        if (isOpen && leasingCostReserves) {
            setNewLeaseData({
                renewalProbability: leasingCostReserves.newLeaseRenewalProbability || 0,
                averageMarketRent: leasingCostReserves.newLeaseAverageRetailMarketRent || defaultAverageMarketRent || 0,
                leaseTerm: leasingCostReserves.newLeaseLeaseTermYears || 0,
                tenantImprovements: leasingCostReserves.newLeaseTenantImprovementsPerSf || 0,
                leasingCommissions: leasingCostReserves.newLeaseLeasingCommissionsPercentage || 0,
                downtimeFreeRent: leasingCostReserves.newLeaseDowntimeFreeRentMonths || 0,
            });

            setRenewalLeaseData({
                renewalProbability: leasingCostReserves.renewalLeaseRenewalProbability || 0,
                averageMarketRent: leasingCostReserves.renewalLeaseAverageRetailMarketRent || defaultAverageMarketRent || 0,
                leaseTerm: leasingCostReserves.renewalLeaseLeaseTermYears || 0,
                tenantImprovements: leasingCostReserves.renewalLeaseTenantImprovementsPerSf || 0,
                leasingCommissions: leasingCostReserves.renewalLeaseLeasingCommissionsPercentage || 0,
                downtimeFreeRent: leasingCostReserves.renewalLeaseDowntimeFreeRentMonths || 0,
            });
        }
    }, [isOpen, leasingCostReserves, defaultAverageMarketRent]);

    // Memoize schema data to prevent infinite loops
    const formData = useMemo((): ILeasingCostReserves => ({
        id: leasingCostReserves?.id,
        newLeaseRenewalProbability: newLeaseData.renewalProbability,
        newLeaseAverageRetailMarketRent: newLeaseData.averageMarketRent,
        newLeaseLeaseTermYears: newLeaseData.leaseTerm,
        newLeaseTenantImprovementsPerSf: newLeaseData.tenantImprovements,
        newLeaseLeasingCommissionsPercentage: newLeaseData.leasingCommissions,
        newLeaseDowntimeFreeRentMonths: newLeaseData.downtimeFreeRent,
        renewalLeaseRenewalProbability: renewalLeaseData.renewalProbability,
        renewalLeaseAverageRetailMarketRent: renewalLeaseData.averageMarketRent,
        renewalLeaseLeaseTermYears: renewalLeaseData.leaseTerm,
        renewalLeaseTenantImprovementsPerSf: renewalLeaseData.tenantImprovements,
        renewalLeaseLeasingCommissionsPercentage: renewalLeaseData.leasingCommissions,
        renewalLeaseDowntimeFreeRentMonths: renewalLeaseData.downtimeFreeRent,
    }), [newLeaseData, renewalLeaseData, leasingCostReserves?.id]);

    const debouncedFormData = useDebounce(formData, 300);

    useEffect(() => {
        if (isOpen && debouncedFormData) {
            handlePreviewLeasingCostReserves(debouncedFormData, previewResult => {
                setPreviewData(previewResult);
            });
        }
    }, [debouncedFormData, isOpen, handlePreviewLeasingCostReserves]);

    useEffect(() => {
        if (isOpen) setPreviewData(null);
    }, [isOpen]);

    const displayData = { ...leasingCostReserves, ...previewData };

    const formatCurrency = (value: number) => numeralFormatterCurrency(value, showCents ? '$0,0.00' : '$0,0');

    const handleUseCalculation = () => {
        handleUpdateLeasingCostReserves(formData, () => {
            setPreviewData(null);
            toggle();
        });
    };

    const subheader = (
        <tr className="NSTable__tbody__subheader font-12 text-muted text-center">
            <td>{`Amount ($/${areaType})`}</td>
            <td>Total</td>
            <td>$/year</td>
        </tr>
    );

    return (
        <NSModal
            isOpen={isOpen}
            toggle={toggle}
            modalHeaderText="Calculate leasing cost reserve"
            modalFooterButtonText="Use calculation"
            modalFooterButtonFunction={handleUseCalculation}
            modalFooterCancelButtonText="Cancel"
            modalFooterCancelButtonFunction={toggle}
            modalHeaderClassName="bg-primary modal-colored-header rounded LeasingCostReservesModal"
            size="xl"
        >
            <div className="LeasingCostReservesContainer">
                <div className="LeasingCostReservesContainer__tables d-flex w-100 align-items-end">
                    {/* Labels Table */}
                    <div className="LeasingCostReservesContainer__labels-table">
                        <NSTable className="NSTable text-dark LeasingCostReservesContainer__table rounded" dark>
                            <tbody>
                                <tr>
                                    <td className="align-middle font-weight-medium">Renewal probability</td>
                                </tr>
                                <tr>
                                    <td className="align-middle font-weight-medium">{`Average market rent (per ${areaType})`}</td>
                                </tr>
                                <tr>
                                    <td className="align-middle font-weight-medium">Lease term</td>
                                </tr>
                                <tr className="NSTable__tbody__subheader">
                                    <td />
                                </tr>
                                <tr>
                                    <td className="align-middle font-weight-medium">Tenant improvements</td>
                                </tr>
                                <tr>
                                    <td className="align-middle font-weight-medium">Leasing commissions</td>
                                </tr>
                                <tr>
                                    <td className="align-middle font-weight-medium">Downtime / free rent</td>
                                </tr>
                            </tbody>
                            <tfoot className="NSTable__tfoot">
                                <tr className="NSTable__tfoot__tr">
                                    <td className="align-middle font-weight-bold">Total leasing costs</td>
                                </tr>
                            </tfoot>
                        </NSTable>
                    </div>

                    {/* New Lease Table */}
                    <div className="LeasingCostReservesContainer__data-table">
                        <div className="LeasingCostReservesContainer__tab text-dark text-center font-12 d-flex align-items-center justify-content-center">
                            New lease
                        </div>
                        <NSTable
                            className="NSTable text-dark LeasingCostReservesContainer__table LeasingCostReservesContainer__table--tabbed rounded"
                            dark
                        >
                            <tbody>
                                {/* Renewal Probability */}
                                <tr>
                                    <td colSpan={3} className="text-left align-middle">
                                        <span className="LeasingCostReservesContainer__renewal-badge rounded">
                                            {numeralFormatter(1 - renewalLeaseData.renewalProbability, '0,0.00%')}
                                        </span>
                                    </td>
                                </tr>

                                {/* Average Market Rent */}
                                <tr>
                                    <td colSpan={3} className="text-left">
                                        <NSInputNumberStepper
                                            name="newLeaseAverageMarketRent"
                                            inputGroupClassName="NSInputNumberStepper__wide-lg ml-0"
                                            value={Number(newLeaseData.averageMarketRent.toFixed(2))}
                                            step={0.01}
                                            customPrefix="$"
                                            onChange={(value: number) => setNewLeaseData({
                                                ...newLeaseData,
                                                averageMarketRent: Number(value.toFixed(2)),
                                            })}
                                        />
                                    </td>
                                </tr>

                                {/* Lease Term */}
                                <tr>
                                    <td colSpan={3} className="text-left lease-term-cell">
                                        <NSInputNumberStepper
                                            name="newLeaseLeaseTerm"
                                            inputGroupClassName="NSInputNumberStepper__wide-lg ml-0"
                                            value={newLeaseData.leaseTerm}
                                            step={1}
                                            customSuffix="years"
                                            onChange={(value: number) => setNewLeaseData({ ...newLeaseData, leaseTerm: value })}
                                        />
                                    </td>
                                </tr>

                                {subheader}

                                {/* Tenant Improvements */}
                                <tr>
                                    <td className="text-center NSTable__tbody__tr__td--right-border">
                                        <NSInputNumberStepper
                                            name="newLeaseTenantImprovements"
                                            inputGroupClassName="NSInputNumberStepper__wide-md"
                                            value={newLeaseData.tenantImprovements}
                                            step={0.01}
                                            customPrefix="$"
                                            onChange={(value: number) => setNewLeaseData({ ...newLeaseData, tenantImprovements: value })}
                                        />
                                    </td>
                                    <td className="text-center NSTable__tbody__tr__td--right-border align-middle">
                                        {formatCurrency(displayData?.newLeaseTenantImprovementsTotal || 0)}
                                    </td>
                                    <td className="text-center align-middle">
                                        {formatCurrency(displayData?.newLeaseTenantImprovementsPerYear || 0)}
                                    </td>
                                </tr>

                                {/* Leasing Commissions */}
                                <tr>
                                    <td className="text-center NSTable__tbody__tr__td--right-border">
                                        <NSInputNumberStepper
                                            name="newLeaseLeasingCommissions"
                                            inputGroupClassName="NSInputNumberStepper__wide-md"
                                            value={newLeaseData.leasingCommissions}
                                            step={0.01}
                                            percentage
                                            onChange={(value: number) => setNewLeaseData({ ...newLeaseData, leasingCommissions: value })}
                                        />
                                    </td>
                                    <td className="text-center NSTable__tbody__tr__td--right-border align-middle">
                                        {formatCurrency(displayData?.newLeaseLeasingCommissionsTotal || 0)}
                                    </td>
                                    <td className="text-center align-middle">
                                        {formatCurrency(displayData?.newLeaseLeasingCommissionsPerYear || 0)}
                                    </td>
                                </tr>

                                {/* Downtime / Free Rent */}
                                <tr>
                                    <td className="text-center NSTable__tbody__tr__td--right-border">
                                        <NSInputNumberStepper
                                            name="newLeaseDowntimeFreeRent"
                                            inputGroupClassName="NSInputNumberStepper__wide-md"
                                            value={newLeaseData.downtimeFreeRent}
                                            step={1}
                                            customSuffix="mo"
                                            onChange={(value: number) => setNewLeaseData({ ...newLeaseData, downtimeFreeRent: value })}
                                        />
                                    </td>
                                    <td className="text-center NSTable__tbody__tr__td--right-border align-middle">
                                        {formatCurrency(displayData?.newLeaseDowntimeFreeRentTotal || 0)}
                                    </td>
                                    <td className="text-center align-middle">
                                        {formatCurrency(displayData?.newLeaseDowntimeFreeRentPerYear || 0)}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot className="NSTable__tfoot">
                                <tr className="NSTable__tfoot__tr">
                                    <td className="NSTable__tfoot__tr__td--right-border" />
                                    <td className="text-center font-weight-bold">
                                        {formatCurrency(displayData?.newLeaseTotalLeasingCosts || 0)}
                                    </td>
                                    <td className="text-center font-weight-bold">
                                        {formatCurrency(displayData?.newLeaseTotalLeasingCostsPerYear || 0)}
                                    </td>
                                </tr>
                            </tfoot>
                        </NSTable>
                    </div>

                    {/* Renewal Lease Table */}
                    <div className="LeasingCostReservesContainer__data-table">
                        <div className="LeasingCostReservesContainer__tab text-dark text-center font-12 d-flex align-items-center justify-content-center">
                            Renewal lease
                        </div>
                        <NSTable
                            className="NSTable text-dark LeasingCostReservesContainer__table LeasingCostReservesContainer__table--tabbed rounded"
                            dark
                        >
                            <tbody>
                                {/* Renewal Probability */}
                                <tr>
                                    <td colSpan={3} className="text-left">
                                        <NSInputNumberStepper
                                            name="renewalLeaseRenewalProbability"
                                            inputGroupClassName="NSInputNumberStepper__wide-lg ml-0"
                                            value={renewalLeaseData.renewalProbability}
                                            step={0.01}
                                            percentage
                                            onChange={(value: number) => setRenewalLeaseData({ ...renewalLeaseData, renewalProbability: value })}
                                        />
                                    </td>
                                </tr>

                                {/* Average Market Rent */}
                                <tr>
                                    <td colSpan={3} className="text-left">
                                        <NSInputNumberStepper
                                            name="renewalLeaseAverageMarketRent"
                                            inputGroupClassName="NSInputNumberStepper__wide-lg ml-0"
                                            value={Number(renewalLeaseData.averageMarketRent.toFixed(2))}
                                            step={0.01}
                                            customPrefix="$"
                                            onChange={(value: number) => setRenewalLeaseData({
                                                ...renewalLeaseData,
                                                averageMarketRent: Number(value.toFixed(2)),
                                            })}
                                        />
                                    </td>
                                </tr>

                                {/* Lease Term */}
                                <tr>
                                    <td colSpan={3} className="text-left lease-term-cell">
                                        <NSInputNumberStepper
                                            name="renewalLeaseLeaseTerm"
                                            inputGroupClassName="NSInputNumberStepper__wide-lg ml-0"
                                            value={renewalLeaseData.leaseTerm}
                                            step={1}
                                            customSuffix="years"
                                            onChange={(value: number) => setRenewalLeaseData({ ...renewalLeaseData, leaseTerm: value })}
                                        />
                                    </td>
                                </tr>

                                {subheader}

                                {/* Tenant Improvements */}
                                <tr>
                                    <td className="text-center NSTable__tbody__tr__td--right-border">
                                        <NSInputNumberStepper
                                            name="renewalLeaseTenantImprovements"
                                            inputGroupClassName="NSInputNumberStepper__wide-md"
                                            value={renewalLeaseData.tenantImprovements}
                                            step={0.01}
                                            customPrefix="$"
                                            onChange={(value: number) => setRenewalLeaseData({ ...renewalLeaseData, tenantImprovements: value })}
                                        />
                                    </td>
                                    <td className="text-center NSTable__tbody__tr__td--right-border align-middle">
                                        {formatCurrency(displayData?.renewalLeaseTenantImprovementsTotal || 0)}
                                    </td>
                                    <td className="text-center align-middle">
                                        {formatCurrency(displayData?.renewalLeaseTenantImprovementsPerYear || 0)}
                                    </td>
                                </tr>

                                {/* Leasing Commissions */}
                                <tr>
                                    <td className="text-center NSTable__tbody__tr__td--right-border">
                                        <NSInputNumberStepper
                                            name="renewalLeaseLeasingCommissions"
                                            inputGroupClassName="NSInputNumberStepper__wide-md"
                                            value={renewalLeaseData.leasingCommissions}
                                            step={0.01}
                                            percentage
                                            onChange={(value: number) => setRenewalLeaseData({ ...renewalLeaseData, leasingCommissions: value })}
                                        />
                                    </td>
                                    <td className="text-center NSTable__tbody__tr__td--right-border align-middle">
                                        {formatCurrency(displayData?.renewalLeaseLeasingCommissionsTotal || 0)}
                                    </td>
                                    <td className="text-center align-middle">
                                        {formatCurrency(displayData?.renewalLeaseLeasingCommissionsPerYear || 0)}
                                    </td>
                                </tr>

                                {/* Downtime / Free Rent */}
                                <tr>
                                    <td className="text-center NSTable__tbody__tr__td--right-border">
                                        <NSInputNumberStepper
                                            name="renewalLeaseDowntimeFreeRent"
                                            inputGroupClassName="NSInputNumberStepper__wide-md"
                                            value={renewalLeaseData.downtimeFreeRent}
                                            step={1}
                                            customSuffix="mo"
                                            onChange={(value: number) => setRenewalLeaseData({ ...renewalLeaseData, downtimeFreeRent: value })}
                                        />
                                    </td>
                                    <td className="text-center NSTable__tbody__tr__td--right-border align-middle">
                                        {formatCurrency(displayData?.renewalLeaseDowntimeFreeRentTotal || 0)}
                                    </td>
                                    <td className="text-center align-middle">
                                        {formatCurrency(displayData?.renewalLeaseDowntimeFreeRentPerYear || 0)}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot className="NSTable__tfoot">
                                <tr className="NSTable__tfoot__tr">
                                    <td className="NSTable__tfoot__tr__td--right-border" />
                                    <td className="text-center font-weight-bold">
                                        {formatCurrency(displayData?.renewalLeaseTotalLeasingCosts || 0)}
                                    </td>
                                    <td className="text-center font-weight-bold">
                                        {formatCurrency(displayData?.renewalLeaseTotalLeasingCostsPerYear || 0)}
                                    </td>
                                </tr>
                            </tfoot>
                        </NSTable>
                    </div>
                </div>
            </div>

            <hr className="my-2" />

            <NSTable className="NSTable text-dark LeasingCostReservesContainer__table rounded">
                <tfoot className="NSTable__tfoot my-2">
                    <tr className="NSTable__tfoot__tr">
                        <td className="align-middle font-weight-bold">Annual leasing cost reserve</td>
                        <td className="align-middle text-right">
                            {formatCurrency(displayData?.totalAmountPerYear || 0)}
                        </td>
                    </tr>
                </tfoot>
            </NSTable>
        </NSModal>
    );
};

export default LeasingCostReservesModal;
